import { Controller } from '@hotwired/stimulus'
import * as echarts from 'echarts'
import { LineChart, PieChart, BarChart } from '../charts'
import { toPng } from 'html-to-image'

const LINE = 'line'
const PIE = 'pie'
const BAR = 'bar'

// Connects to data-controller="charts"
export default class extends Controller {
  static targets = ['container', 'legend', 'download']
  static values = {
    type: String,
    xAxisType: {
      type: String,
      default: 'category'
    },
    withLegend: {
      type: Boolean,
      default: false
    },
    dataset: {
      type: Array,
      default: []
    },
    period: String,
    units: String,
    withDrawer: {
      type: Boolean,
      default: false
    }
  }

  chart = null

  connect() {
    if (this.hasContainerTarget) {
      this.initChart()
    }
  }

  initChart() {
    this.chart = echarts.init(this.containerTarget, null, { renderer: 'svg' })

    switch (this.typeValue) {
      case LINE:
        this.chartInstance = new LineChart(this)
        break
      case PIE:
        this.chartInstance = new PieChart(this)
        break
      case BAR:
        this.chartInstance = new BarChart(this)
        break
      default:
        throw 'Unknown chart type'
    }

    this.chartInstance.setChart()
    if (this.hasDownloadTarget) {
      this.downloadTarget.addEventListener('click', this.downloadChart.bind(this))
    }

    if (this.withDrawerValue) {
      this.chart.on('click', this.loadFrame.bind(this))
    }

    this.setResizeListeners()
  }

  setResizeListeners() {
    const resizeHandler = () => {
      this.chart.resize()
      if (this.typeValue === LINE) {
        this.adjustLabelRotation()
      }
    }

    window.addEventListener('resize', resizeHandler)
    window.addEventListener('turbo:frame-load', resizeHandler)
    document.addEventListener('sidebar:resize', resizeHandler)
  }

  adjustLabelRotation() {
    const chartWidth = this.chart.getWidth()
    const chartRotationMinWidth = 800
    const rotationAngle = chartWidth < chartRotationMinWidth ? 45 : 0

    this.chart.setOption({
      xAxis: {
        axisLabel: {
          rotate: rotationAngle
        }
      }
    })
  }

  downloadChart() {
    const element = this.element

    const filter = (node) => {
      return !(node instanceof HTMLElement && node.dataset.chartsTarget === 'download')
    }

    const title = element.querySelector('h4')?.textContent?.trim()
    const period = this.periodValue?.replace(/\//g, '-')
    const filename = `${title} chart (${period}).png`
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1
    const options = { filter: filter }

    if (isFirefox) {
      options.skipFonts = true
    }

    toPng(element, options)
      .then((dataUrl) => {
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = filename
        link.click()
      })
      .catch((error) => {
        console.error('An error occurred while generating the image:', error)
      })
  }

  loadFrame(event) {
    fetch(event.value.url, {
      method: 'GET',
       headers: {
         'Accept': 'text/vnd.turbo-stream.html'
       }
     })
       .then(response => response.text())
       .then(html => Turbo.renderStreamMessage(html))
  }
}
